import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const seoCache = {};

function setMeta(name, content, attr = 'name') {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export default function SEOHead({ pageSlug, fallback = {} }) {
  const [seo, setSeo] = useState(fallback);

  useEffect(() => {
    if (!pageSlug) return;
    if (seoCache[pageSlug]) {
      setSeo(seoCache[pageSlug]);
      return;
    }
    fetch(`${API}/api/seo/pages/${pageSlug}`)
      .then(r => r.json())
      .then(data => {
        seoCache[pageSlug] = data;
        setSeo(data);
      })
      .catch(() => {});
  }, [pageSlug]);

  useEffect(() => {
    const title = seo.title || fallback.title || '';
    const description = seo.description || fallback.description || '';
    const keywords = seo.keywords || fallback.keywords || '';
    const ogTitle = seo.og_title || title;
    const ogDescription = seo.og_description || description;
    const ogImage = seo.og_image || '';
    const canonicalUrl = seo.canonical_url || '';
    const structuredDataType = seo.structured_data_type || 'WebPage';

    // Title
    if (title) document.title = title;

    // Meta tags
    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('content-language', 'uk', 'http-equiv');

    // Open Graph
    setMeta('og:type', 'website', 'property');
    setMeta('og:title', ogTitle, 'property');
    setMeta('og:description', ogDescription, 'property');
    if (ogImage) setMeta('og:image', ogImage, 'property');
    setMeta('og:locale', 'uk_UA', 'property');
    setMeta('og:site_name', 'CargoAgs', 'property');

    // Twitter
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', ogTitle);
    if (ogDescription) setMeta('twitter:description', ogDescription);
    if (ogImage) setMeta('twitter:image', ogImage);

    // Canonical
    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalUrl) {
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute('href', canonicalUrl);
    }

    // JSON-LD Structured Data
    const existingScript = document.querySelector('script[data-seo-jsonld]');
    if (existingScript) existingScript.remove();

    const getStructuredData = () => {
      const base = { "@context": "https://schema.org" };
      const logoUrl = "https://cargoags.com/logo-512.png";
      if (structuredDataType === 'Organization') {
        return { ...base, "@type": "Organization", "name": "CargoAgs", "description": description,
          "url": "https://cargoags.com",
          "logo": { "@type": "ImageObject", "url": logoUrl, "width": 512, "height": 512 },
          "image": logoUrl,
          "contactPoint": { "@type": "ContactPoint", "telephone": "+380501234567", "contactType": "customer service", "areaServed": "UA", "availableLanguage": ["Ukrainian", "Russian"] }
        };
      }
      if (structuredDataType === 'Store') {
        return { ...base, "@type": "Store", "name": "CargoAgs — Інтернет-магазин", "description": description,
          "url": "https://cargoags.com/shop", "priceRange": "$$", "logo": logoUrl, "image": logoUrl,
          "address": { "@type": "PostalAddress", "addressCountry": "UA" }
        };
      }
      if (structuredDataType === 'Service') {
        return { ...base, "@type": "Service", "name": "Доставка товарів з США — CargoAgs", "description": description,
          "provider": { "@type": "Organization", "name": "CargoAgs", "logo": logoUrl }, "areaServed": "UA", "serviceType": "Cargo delivery"
        };
      }
      return { ...base, "@type": "WebPage", "name": title, "description": description };
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', 'true');
    script.textContent = JSON.stringify(getStructuredData());
    document.head.appendChild(script);

    return () => {
      const el = document.querySelector('script[data-seo-jsonld]');
      if (el) el.remove();
    };
  }, [seo, fallback]);

  return null;
}
